import * as R from 'ramda';
import createError from 'http-errors';
import type {
  ConfigurationTemplate, ConfigurationTemplateResponse, PartialCategoryComponent,
  SystemConfiguration,
  SystemConfigurationDefault, SystemConfigurator,
  TemplateCategory, TemplateCategoryComponent, TemplateCategoryComponentProduct, TemplateField
} from '@/types/exceleraSystems';
import {getModelById} from '@/server/data/systemModels.ts';
import {exceleraApiClient} from '@/lib/fetchClient.ts';
import { allowNone, calcComponentSubtotal } from '@/components/helpers/configuratorHelpers.ts';

const templates = {};

const NONE_COMPONENT: TemplateCategoryComponent = {
  _id: `none__`,
  product: {
    _id: '__none',
    manufacturer: { name: '' },
    cpAsCents: 0,
    spAsCents: 0,
    description: 'None'
  }
};

const getSubtotal: (component: TemplateCategoryComponent) => number = R.propOr(0, 'subtotal');
const getQty: (config: SystemConfigurationDefault | undefined) => number = R.propOr(0, 'qty');

const getSelectedComponentPrice: (components: TemplateCategoryComponent[]) => number = R.pipe(
  R.find(R.propOr(false, 'isSelected')),
  getSubtotal,
);

const calcComponentPriceDiffs: (isSelectedPrice: number) => (component: TemplateCategoryComponent) => TemplateCategoryComponent = (isSelectedPrice) => (component) => {
  const diffPrice = Math.round(getSubtotal(component) - isSelectedPrice);

  const displayDiffPrice = (diffPrice < 0) ? `subtract ${diffPrice*-1}` : `add ${diffPrice}`;
  return {
    ...component,
    displayDiffPrice,
  }
}
const calcAllComponentPriceDiffs: (components: TemplateCategoryComponent[], isSelectedPrice: number) => TemplateCategoryComponent[] = (components, isSelectedPrice) => R.map(calcComponentPriceDiffs(isSelectedPrice))(components);

const calcPriceDiffs: (components: TemplateCategoryComponent[]) => TemplateCategoryComponent[] = R.converge(
  calcAllComponentPriceDiffs,
  [R.identity, getSelectedComponentPrice]
);

export const transformTemplateCategory: (category: TemplateCategory) => TemplateCategory = R.evolve({
  components: calcPriceDiffs,
});


const addNoneOptionToCategory: (category: TemplateCategory) => TemplateCategory = R.when(
  allowNone,
  R.evolve({
    components: R.append(NONE_COMPONENT)
  }),
);

const addNoneToCategories: (categories: TemplateCategory[]) => TemplateCategory[] = R.map(addNoneOptionToCategory);

const addNoneOptionsToTemplate: (template: ConfigurationTemplate) => ConfigurationTemplate =
  R.evolve({
    categories: addNoneToCategories
  });

export async function initTemplate(templateId: string) {
  const data: ConfigurationTemplateResponse = await exceleraApiClient()(`/wapi/v1/cms/systems/templates/${templateId}`);
  templates[templateId] = addNoneOptionsToTemplate(data.template);
}


const cleanupProduct: (component: TemplateCategoryComponent) => TemplateCategoryComponentProduct = R.pipe(R.prop('product'),R.omit(['cp', 'sp']));

const cleanupComponent: (markup: number, margin: number) => (component: TemplateCategoryComponent) => PartialCategoryComponent = (markup, margin) => R.applySpec({
  subtotal: calcComponentSubtotal(markup, margin),
  product: cleanupProduct
});

const transformComponent: (markup: number, margin: number) => (component: TemplateCategoryComponent) => TemplateCategoryComponent = (markup, margin) => R.converge(R.mergeDeepRight, [R.identity, cleanupComponent(markup, margin)]);

const applyConfigurationToComponents = (config: SystemConfigurationDefault, markup: number, margin: number) => (component: TemplateCategoryComponent): TemplateCategoryComponent => {
  let isSelected = false;
  if(config.component === component._id) {
    isSelected = true;
  }

  return transformComponent(markup, margin)({
    ...component,
    isSelected,
  });
}

const applyNoneToComponents =(markup: number, margin: number) => (component: TemplateCategoryComponent): TemplateCategoryComponent => {
  let isSelected = false;
  if(component._id === 'none__') {
    isSelected = true;
  }
  return transformComponent(markup, margin)({
    ...component,
    isSelected,
  });
}

const getFieldName = (categoryName: string): string => {
  switch(categoryName) {
    case 'Memory':
      return 'Memory Slots';
    case 'Processors':
      return 'Processor Sockets';
    case 'Video Cards':
      return 'Video Card Slots';
    default:
      return categoryName;
  }
}
const getMaxQty = (categoryName: string, fields: TemplateField[]): number => {
  const fieldName = getFieldName(categoryName);
  const field = fields.find(f => f.name === fieldName);
  // Assume all field.value are numbers
  return field ? parseInt(field.value) : 1;
}

const applyConfigurationToCategory: (configuration: SystemConfiguration) => (category: TemplateCategory) => TemplateCategory = (configuration) => (category) => {
  const { markup, margin } = configuration;
  const fields: TemplateField[]  = R.propOr([], 'fields')(configuration);
  const maxQty = getMaxQty(category.category.name, fields);
  const config = configuration.configuration.find(c => c.stc === category._id);
  const qty = getQty(config);
  const components = config
    ? R.map(applyConfigurationToComponents(config, markup, margin))(category.components)
    : R.map(applyNoneToComponents(markup, margin))(category.components)

  return {
    ...category,
    qty,
    maxQty,
    components,
  }
}

const applyConfiguration: (configuration: SystemConfiguration) => (template: ConfigurationTemplate) => ConfigurationTemplate =(configuration) => R.evolve({
  categories: R.map(applyConfigurationToCategory(configuration))
});

const transformTemplate: (systemDetails: SystemConfiguration) => (template: ConfigurationTemplate) => ConfigurationTemplate = (systemDetails) => R.pipe(
  applyConfiguration(systemDetails),
  R.evolve({
    categories: R.map(transformTemplateCategory)
  })
)

export function getConfigurator(modelId: string, isInternal: boolean = false): SystemConfigurator {
  const systemDetails: SystemConfiguration = getModelById(modelId);
  if(systemDetails) {
    const template: ConfigurationTemplate = templates[systemDetails.template];

    const omitFields = isInternal ? ['configuration', '_entered', '_template'] : ['margin', 'markup', 'configuration', '_entered', 'template'];

    if(template) {
      return {
        systemDetails: R.omit(omitFields)(systemDetails),
        template: transformTemplate(systemDetails)(template)
      }
    }
  }
  throw createError(404, `The configurator for _id = ${modelId} was not found.`)
}
